/**
 * Sessions store using Zustand
 */

import { create } from 'zustand';
import {
  loadSessions as apiLoadSessions,
  createSession as apiCreateSession,
  deleteSession as apiDeleteSession,
} from '../lib/api';
import { Session } from '../lib/types';

interface SessionsState {
  activeSessions: Session[];
  currentSite: number | null;
  currentNovaAccountId: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentSite: (siteId: number | null) => void;
  setCurrentNovaAccountId: (accountId: string | null) => void;
  clearSessions: () => void;
  loadSessions: () => Promise<void>;
  refreshSessions: () => Promise<void>; // Auto-refresh without loading state
  createSession: (data: {
    clipAddress: string;
    clipName: string;
    clientName: string;
  }) => Promise<number>;
  deleteSession: (sessionId: number) => Promise<void>;
}

export const useSessionsStore = create<SessionsState>((set, get) => ({
  activeSessions: [],
  currentSite: null,
  currentNovaAccountId: null,
  isLoading: false,
  error: null,

  setCurrentSite: (siteId: number | null) => {
    set({ currentSite: siteId });
  },

  setCurrentNovaAccountId: (accountId: string | null) => {
    set({ currentNovaAccountId: accountId });
  },

  clearSessions: () => {
    set({ activeSessions: [], isLoading: false, error: null });
  },

  loadSessions: async () => {
    const { currentSite, currentNovaAccountId } = get();
    if (!currentSite) {
      set({ error: 'Please select a site' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const data = await apiLoadSessions(currentSite, { novaAccountId: currentNovaAccountId });
      set({ activeSessions: data.statuses || [], isLoading: false });
    } catch (error: any) {
      console.error('Failed to load sessions:', error);
      const isSessionExpired = error?.message === 'Session expired';
      set({
        error: isSessionExpired ? null : error.message || 'Failed to load sessions',
        isLoading: false,
        activeSessions: isSessionExpired ? get().activeSessions : [],
      });
    }
  },

  // Silently refresh without loading state (for auto-refresh)
  refreshSessions: async () => {
    const { currentSite, currentNovaAccountId } = get();
    if (!currentSite) return;

    try {
      const data = await apiLoadSessions(currentSite, { novaAccountId: currentNovaAccountId });
      set({ activeSessions: data.statuses || [] });
    } catch (error: any) {
      console.error('Auto-refresh failed:', error);
      // Don't throw or show error for auto-refresh
    }
  },

  createSession: async (data) => {
    const { currentSite, currentNovaAccountId } = get();
    if (!currentSite) throw new Error('No site selected');

    set({ isLoading: true, error: null });
    try {
      // Create session (no initial packet - matches Android app behavior)
      const sessionId = await apiCreateSession({
        siteId: currentSite,
        clipAddress: data.clipAddress.toUpperCase(),
        clipName: data.clipName,
        client: data.clientName,
        sex: 'M',
        criterionId: 1,
      }, { novaAccountId: currentNovaAccountId });

      set({ isLoading: false });

      // Reload sessions after a brief delay
      setTimeout(() => get().loadSessions(), 1000);

      return sessionId;
    } catch (error: any) {
      console.error('Failed to create session:', error);
      set({
        error: error.message || 'Failed to create session',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteSession: async (sessionId: number) => {
    const { currentNovaAccountId } = get();
    set({ isLoading: true, error: null });
    try {
      await apiDeleteSession(sessionId, { novaAccountId: currentNovaAccountId });
      set({ isLoading: false });

      // Reload sessions
      setTimeout(() => get().loadSessions(), 500);
    } catch (error: any) {
      console.error('Failed to delete session:', error);
      set({
        error: error.message || 'Failed to delete session',
        isLoading: false,
      });
      throw error;
    }
  },
}));
